/* eslint-disable react/jsx-props-no-spreading */
import {
  Box,
  FormHelperText,
  Input,
  Stack,
  Textarea,
  useColorModeValue,
} from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client';
import { JobSeeker } from '~/generated';
import { myProfile, updateProfile } from '~/queries';
import { notifySaveFailure, notifySaveSuccess } from '~/components/atoms';

import { InputContainer } from '~/components/molecules';

type FocusFormInput = {
  focusSummary: string | undefined;
  focusDetail: string | undefined;
  thingsChangedAfterCovid: string | undefined;
};

const FocusForm: React.FC = () => {
  const { data, loading } = useQuery<{ jobSeekerMe: JobSeeker }>(myProfile);
  const [updateSeekerProfile, { error }] = useMutation(updateProfile);

  const cardBg = useColorModeValue('white', 'gray.800');
  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = useForm<FocusFormInput>({
    mode: 'onBlur',
    shouldFocusError: false,
  });
  useEffect(() => {
    if (!loading && !isDirty) {
      const defaultValues = {
        focusSummary: data?.jobSeekerMe?.focusSummary || undefined,
        focusDetail: data?.jobSeekerMe?.focusDetail || undefined,
        thingsChangedAfterCovid:
          data?.jobSeekerMe?.thingsChangedAfterCovid || undefined,
      };
      reset({
        ...defaultValues,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);
  const onSubmit: SubmitHandler<FocusFormInput> = async (input) => {
    await updateSeekerProfile({
      variables: {
        input,
      },
    });
    if (error) {
      notifySaveFailure();
    } else {
      notifySaveSuccess();
    }
  };

  return (
    <Box shadow="base" rounded={[null, 'md']} overflow={{ sm: 'hidden' }}>
      <Stack
        px={4}
        py={5}
        bg={useColorModeValue('white', 'gray.700')}
        spacing={6}
        p={{ sm: 8 }}
      >
        <form onBlur={handleSubmit(onSubmit)}>
          <Box
            maxW="600px"
            w="full"
            bg={cardBg}
            rounded="md"
            overflow="hidden"
            borderStyle="solid"
          >
            <InputContainer
              label="学生時代に力を入れたこと（概要）"
              name="focusSummary"
              invalid={!!errors.focusSummary}
            >
              <Input
                {...register('focusSummary', { maxLength: 50 })}
                maxLength={50}
                isInvalid={!!errors.focusSummary}
              />
              <FormHelperText marginTop="1px" marginBottom="10px">
                ~50文字
              </FormHelperText>
            </InputContainer>

            <InputContainer
              label="学生時代に力を入れたこと（詳細）"
              name="focusDetail"
              invalid={!!errors.focusDetail}
            >
              <Textarea
                {...register('focusDetail', { minLength: 50, maxLength: 800 })}
                isInvalid={!!errors.focusDetail}
              />
              <FormHelperText marginTop="1px" marginBottom="10px">
                50~800文字
              </FormHelperText>
            </InputContainer>

            <InputContainer
              label="コロナ禍で変化した意識や行動"
              name="thingsChangedAfterCovid"
              invalid={!!errors.thingsChangedAfterCovid}
            >
              <Textarea
                {...register('thingsChangedAfterCovid', {
                  minLength: 50,
                  maxLength: 800,
                })}
                isInvalid={!!errors.thingsChangedAfterCovid}
              />
              <FormHelperText marginTop="1px" marginBottom="10px">
                50~800文字
              </FormHelperText>
            </InputContainer>
          </Box>
        </form>
      </Stack>
    </Box>
  );
};

export default FocusForm;
